'use client';
import { API_ROUTES } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useFetchGames } from '@shared/hooks';
import { Vip } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useClientConfig, useIsMobile, useMediaQuery } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import type { ComponentProps } from 'react';
import { CategoryDetailsButton } from '../../CategoryDetailsButton/CategoryDetailsButton';
import type { GamesCategoryFilter } from '../../games/types/types';
import { Carousel, JackpotContainer, JackpotHeader, Jackpots, Title } from '../components';
import { useFetchJackpots } from '../hooks';
import type { JackpotType, JackpotVariant } from '../types';

const TRANSLATIONS_NAMESPACE = 'casino.jackpot_overdrive';
const JACKPOT_REFRESH_INTERVAL = 180_000;
const RED_TIGER_LOGO = 'https://media.ffycdn.net/eu/lottoland/FCS21DXXXGcT8rTD8Tba.png';

type JackpotOverdriveJackpots = 'jumbo' | 'daily' | 'rapid';
type JackpotOverdriveJackpotsData = Record<
  JackpotOverdriveJackpots,
  {
    value: string;
    previousValue: string;
    wonValue: string;
    status: string;
  }
>;

type JackpotOverdriveProps = ComponentProps<'div'> & {
  isPage?: boolean;
  gamesFilter: GamesCategoryFilter;
};

/**
 * Maps a jackpot data to a JackpotType object, used to render the jackpot info component.
 * @param key - The jackpot key (jumbo, daily, rapid)
 * @param current - The current jackpot value
 * @param previous - The previous jackpot value
 * @param t - The translations function
 * @param refreshInterval - The refresh interval
 * @param variant - The variant of the jackpot overdrive section (default or sticky)
 * @returns The jackpot object
 */
function mapJackpot(key: string, current: string, previous: string, t: ReturnType<typeof useTranslations>, refreshInterval: number, variant: JackpotVariant): JackpotType {
  return {
    label: t(key),
    value: Number.parseInt(current),
    previousValue: Number.parseInt(previous),
    refreshInterval,
    highlight: key === 'jumbo',
    icon: key === 'jumbo' ? <Vip size="medium" /> : undefined,
    variant,
  };
}

/**
 * JackpotOverdrive component displays the jackpot overdrive section.
 * @param isPage - Whether the jackpot overdrive section is on a dedicated page (true) or not (false)
 * @returns The JackpotOverdrive component
 */
function JackpotOverdrive({ isPage = false, gamesFilter, ...props }: JackpotOverdriveProps) {
  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';
  const t = useTranslations(TRANSLATIONS_NAMESPACE);
  const { currency } = useClientConfig();
  const { data } = useFetchJackpots<JackpotOverdriveJackpotsData>(API_ROUTES.GAMES.JACKPOT_OVERDRIVE.replace(':currency', currency), JACKPOT_REFRESH_INTERVAL);
  const isLargeScreen = useMediaQuery(`(width >= 1440px)`);
  const { games } = useFetchGames({ ...gamesFilter, isLoggedIn, limit: isLargeScreen ? 30 : 15 });
  const isMobile = useIsMobile({ breakpoint: 'md' });
  const jpPageVariant: JackpotVariant = isMobile ? 'default' : 'sticky';
  const variant: JackpotVariant = isPage ? jpPageVariant : 'default';

  const mappedJackpots = data ? Object.entries(data).map(([key, value]) => mapJackpot(key, value.value, value.previousValue, t, JACKPOT_REFRESH_INTERVAL, variant)) : [];

  const gamesListWithId = games?.map((_, index) => ({
    ..._,
    id: index + 1,
  }));

  return (
    <JackpotContainer variant={variant} data-track-container="Jackpot Overdrive" data-testid="jackpot-overdrive-section" {...props}>
      <JackpotHeader variant={variant}>
        <Title variant={variant} namespace={TRANSLATIONS_NAMESPACE} className="text-accent-green sr-only" />
        <Image
          src={RED_TIGER_LOGO}
          alt="Jackpot Overdrive - Red Tiger"
          unoptimized
          width={438}
          height={228}
          className={twMerge('mx-auto object-contain', variant === 'default' ? 'w-100' : 'w-50')}
        />
        <Jackpots layout="overdrive" jackpots={mappedJackpots} variant={variant} />
      </JackpotHeader>
      {variant === 'default' && !isPage ? (
        <Carousel
          namespace={TRANSLATIONS_NAMESPACE}
          games={gamesListWithId ?? []}
          btnMore={<CategoryDetailsButton namespace={'casino'} category={'JPOverdrive'} buttonVariant="primary" />}
        />
      ) : null}
    </JackpotContainer>
  );
}

export { JackpotOverdrive, mapJackpot };
